import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit,
  PhotoCamera,
  Save,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../services/userService';
import { locationService } from '../../services/locationService';
import { birthdayService } from '../../services/birthdayService';
import { formatPhoneOnInput } from '../../utils/phoneFormatter';

const DJProfileManagement = () => {
  const { user, updateUser, changePassword } = useAuth();
  
  // Estilo comum para campos de texto
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#9D00FF',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#9D00FF',
    },
    '& .MuiOutlinedInput-input': {
      color: 'white',
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  };

  // Estilo para campos Select
  const selectFieldStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#9D00FF',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#9D00FF',
    },
    '& .MuiSelect-select': {
      color: 'white',
    },
  };
  const [openDialog, setOpenDialog] = useState(false);
  const [photoDialog, setPhotoDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentPhoto, setCurrentPhoto] = useState(user?.profile_image);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    state: user?.state || '',
    city: user?.city || '',
    whatsapp: user?.whatsapp || '',
    facebook: user?.facebook || '',
    instagram: user?.instagram || '',
    birthday_day: user?.birthday_day || '',
    birthday_month: user?.birthday_month || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();


  // Sincronizar foto quando o usuário mudar
  useEffect(() => {
    if (user?.profile_image) {
      setCurrentPhoto(user.profile_image);
    }
  }, [user?.profile_image]);

  // Carregar estados ao montar o componente
  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const statesData = await locationService.getStates();
        setStates(statesData);
        setFilteredStates(statesData);
      } catch (error) {
        setSnackbar({ open: true, message: 'Erro ao carregar lista de estados', severity: 'error' });
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  const updateProfileMutation = useMutation({
    mutationFn: (data) => userService.updateProfile(data),
    onSuccess: (response) => {
      updateUser(response.data.user);
      queryClient.invalidateQueries(['user']);
      setSnackbar({ open: true, message: 'Perfil atualizado com sucesso!', severity: 'success' });
      setOpenDialog(false);
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar perfil', severity: 'error' });
    }
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: (formData) => userService.uploadProfilePhoto(formData),
    onSuccess: (response) => {
      // Atualizar o usuário no contexto
      updateUser(response.data.user);
      queryClient.invalidateQueries(['user']);
      
      // Atualizar a foto local imediatamente com timestamp para forçar reload
      const photoUrl = response.data.user.profile_image;
      const timestamp = new Date().getTime();
      setCurrentPhoto(`${photoUrl}?t=${timestamp}`);
      
      // Fechar o modal
      setPhotoDialog(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      
      // Mostrar mensagem de sucesso
      setSnackbar({ 
        open: true, 
        message: 'Foto de perfil atualizada com sucesso!', 
        severity: 'success' 
      });
    },
    onError: (error) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Erro ao fazer upload da foto', 
        severity: 'error' 
      });
    }
  });

  const handleOpenDialog = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      state: user?.state || '',
      city: user?.city || '',
      whatsapp: user?.whatsapp || '',
      facebook: user?.facebook || '',
      instagram: user?.instagram || '',
      birthday_day: user?.birthday_day || '',
      birthday_month: user?.birthday_month || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = () => {
    updateProfileMutation.mutate(formData);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });

    // Validação em tempo real para confirmação de senha
    if (name === 'confirmPassword' && passwordData.newPassword && value) {
      if (passwordData.newPassword !== value) {
        console.log('⚠️ Senhas não coincidem em tempo real');
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); // Prevenir o comportamento padrão do formulário
    
    console.log('🔐 Iniciando alteração de senha...');
    console.log('🔐 Dados da senha:', {
      currentPassword: passwordData.currentPassword ? '***' : 'vazio',
      newPassword: passwordData.newPassword ? '***' : 'vazio',
      confirmPassword: passwordData.confirmPassword ? '***' : 'vazio'
    });
    
    // Validações básicas
    if (!passwordData.currentPassword) {
      console.log('❌ Senha atual vazia');
      setSnackbar({ open: true, message: 'Digite sua senha atual', severity: 'error' });
      return;
    }

    if (!passwordData.newPassword) {
      console.log('❌ Nova senha vazia');
      setSnackbar({ open: true, message: 'Digite a nova senha', severity: 'error' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      console.log('❌ Senhas não coincidem');
      setSnackbar({ open: true, message: 'As senhas não coincidem', severity: 'error' });
      toast.error('As senhas não coincidem');
      return;
    }

    // Validação adicional para garantir que as senhas são iguais
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      console.log('❌ Validação dupla: Senhas não coincidem');
      setSnackbar({ open: true, message: 'As senhas não coincidem. Verifique novamente.', severity: 'error' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      console.log('❌ Senha muito curta');
      setSnackbar({ open: true, message: 'A nova senha deve ter pelo menos 6 caracteres', severity: 'error' });
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    // Verificar se a nova senha é diferente da atual
    if (passwordData.currentPassword === passwordData.newPassword) {
      console.log('❌ Nova senha igual à atual');
      setSnackbar({ open: true, message: 'A nova senha deve ser diferente da senha atual', severity: 'error' });
      toast.error('A nova senha deve ser diferente da senha atual');
      return;
    }

    try {
      console.log('🚀 Chamando changePassword...');
      const result = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      console.log('📋 Resultado da alteração:', result);

      if (result.success) {
        console.log('✅ Senha alterada com sucesso!');
        setSnackbar({ 
          open: true, 
          message: '🎉 Perfeito! Sua senha foi alterada com sucesso. Agora você pode fazer login com a nova senha.', 
          severity: 'success' 
        });
        toast.success('Senha alterada com sucesso!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordFields(false);
      } else {
        console.log('❌ Erro na alteração:', result.error);
        setSnackbar({ open: true, message: result.error, severity: 'error' });
        toast.error(result.error);
      }
    } catch (error) {
      console.error('💥 Erro na alteração de senha:', error);
      setSnackbar({ open: true, message: 'Erro ao alterar senha', severity: 'error' });
      toast.error('Erro ao alterar senha');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar formatação automática para telefone e WhatsApp
    let formattedValue = value;
    if (name === 'phone' || name === 'whatsapp') {
      formattedValue = formatPhoneOnInput(value);
    }
    
    setFormData({
      ...formData,
      [name]: formattedValue
    });
  };

  // Manipular seleção de estado
  const handleStateChange = async (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        state: newValue.sigla,
        city: '' // Limpar cidade quando mudar estado
      });
      
      // Carregar cidades do estado selecionado
      setLoadingCities(true);
      try {
        const citiesData = await locationService.getCitiesByState(newValue.id);
        setCities(citiesData);
        setFilteredCities(citiesData);
      } catch (error) {
        setSnackbar({ open: true, message: 'Erro ao carregar lista de cidades', severity: 'error' });
      } finally {
        setLoadingCities(false);
      }
    } else {
      setFormData({
        ...formData,
        state: '',
        city: ''
      });
      setCities([]);
      setFilteredCities([]);
    }
  };

  // Filtrar estados durante a digitação
  const handleStateInputChange = (event, newInputValue) => {
    const filtered = locationService.filterStates(states, newInputValue);
    setFilteredStates(filtered);
  };

  // Manipular seleção de cidade
  const handleCityChange = (event, newValue) => {
    setFormData({
      ...formData,
      city: newValue ? newValue.nome : ''
    });
  };

  // Filtrar cidades durante a digitação
  const handleCityInputChange = (event, newInputValue) => {
    const filtered = locationService.filterCities(cities, newInputValue);
    setFilteredCities(filtered);
  };

  const handleOpenPhotoDialog = () => {
    setSelectedFile(null);
    setPreviewUrl(user?.profile_image || null);
    setPhotoDialog(true);
  };

  const handleClosePhotoDialog = () => {
    setPhotoDialog(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    // Limpar o input de arquivo
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({ 
          open: true, 
          message: 'Formato de arquivo inválido. Use apenas JPEG, JPG ou PNG.', 
          severity: 'error' 
        });
        // Limpar o input
        event.target.value = '';
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'Arquivo muito grande. Tamanho máximo: 5MB.', 
          severity: 'error' 
        });
        // Limpar o input
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadPhoto = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('profile_image', selectedFile);
      uploadPhotoMutation.mutate(formData);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#9D00FF' }}>
              Meu Perfil
            </Typography>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={handleOpenDialog}
              sx={{
                bgcolor: '#9D00FF',
                '&:hover': { bgcolor: '#C733FF' }
              }}
            >
              Editar Perfil
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box position="relative">
                  <Avatar
                    src={currentPhoto || user?.profile_image}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: '#9D00FF',
                      mb: 2,
                      fontSize: '2rem'
                    }}
                  >
                    {user?.name?.charAt(0)}
                  </Avatar>
                  <IconButton
                    onClick={handleOpenPhotoDialog}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: '#9D00FF',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#C733FF',
                      },
                      width: 40,
                      height: 40
                    }}
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {user?.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Nome do DJ
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.name || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.email || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.phone || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Estado
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.state || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Cidade
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.city && user?.state ? `${user.city} - ${user.state}` : user?.city || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Aniversário
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.birthday_day && user?.birthday_month 
                      ? birthdayService.formatBirthday(user.birthday_day, user.birthday_month)
                      : 'Não informado'
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    WhatsApp
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.whatsapp || 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Facebook
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.facebook ? (
                      <a href={user.facebook} target="_blank" rel="noopener noreferrer" style={{ color: '#00D4FF' }}>
                        Ver perfil
                      </a>
                    ) : 'Não informado'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Instagram
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.instagram ? (
                      <a href={`https://instagram.com/${user.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" style={{ color: '#00D4FF' }}>
                        {user.instagram}
                      </a>
                    ) : 'Não informado'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>


      {/* Dialog para Editar Perfil */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Editar Perfil
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do DJ *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  helperText="Ex: DJ João Silva"
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  helperText="Ex: (11) 99999-9999"
                  placeholder="(11) 99999-9999"
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={filteredStates}
                  getOptionLabel={(option) => option ? `${option.nome} - ${option.sigla}` : ''}
                  value={states.find(state => state.sigla === formData.state) || null}
                  onChange={handleStateChange}
                  onInputChange={handleStateInputChange}
                  loading={loadingStates}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado"
                      placeholder="Digite o nome do estado"
                      helperText="Ex: São Paulo - SP"
                      sx={textFieldStyle}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.nome} - {option.sigla}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={filteredCities}
                  getOptionLabel={(option) => option ? option.nome : ''}
                  value={cities.find(city => city.nome === formData.city) || null}
                  onChange={handleCityChange}
                  onInputChange={handleCityInputChange}
                  loading={loadingCities}
                  disabled={!formData.state}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cidade"
                      placeholder="Digite o nome da cidade"
                      helperText={!formData.state ? "Selecione um estado primeiro" : "Ex: São Paulo"}
                      sx={textFieldStyle}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.nome}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  helperText="Ex: (11) 99999-9999"
                  placeholder="(11) 99999-9999"
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  helperText="Ex: https://facebook.com/seuperfil"
                  placeholder="https://facebook.com/seuperfil"
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  helperText="Ex: @seuusuario"
                  placeholder="@seuusuario"
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={selectFieldStyle}>
                  <InputLabel>Dia do Nascimento</InputLabel>
                  <Select
                    name="birthday_day"
                    value={formData.birthday_day}
                    onChange={handleChange}
                    label="Dia do Nascimento"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <MenuItem key={day} value={day}>
                        {day.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={selectFieldStyle}>
                  <InputLabel>Mês do Nascimento</InputLabel>
                  <Select
                    name="birthday_month"
                    value={formData.birthday_month}
                    onChange={handleChange}
                    label="Mês do Nascimento"
                  >
                    <MenuItem value={1}>Janeiro</MenuItem>
                    <MenuItem value={2}>Fevereiro</MenuItem>
                    <MenuItem value={3}>Março</MenuItem>
                    <MenuItem value={4}>Abril</MenuItem>
                    <MenuItem value={5}>Maio</MenuItem>
                    <MenuItem value={6}>Junho</MenuItem>
                    <MenuItem value={7}>Julho</MenuItem>
                    <MenuItem value={8}>Agosto</MenuItem>
                    <MenuItem value={9}>Setembro</MenuItem>
                    <MenuItem value={10}>Outubro</MenuItem>
                    <MenuItem value={11}>Novembro</MenuItem>
                    <MenuItem value={12}>Dezembro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Seção de Alteração de Senha */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ color: 'error.main', fontFamily: 'Orbitron' }}>
                    Alterar Senha
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => setShowPasswordFields(!showPasswordFields)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'error.light',
                        borderColor: 'error.dark',
                      },
                    }}
                  >
                    {showPasswordFields ? 'Cancelar' : 'Alterar Senha'}
                  </Button>
                </Box>
                
                {showPasswordFields && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Senha Atual"
                        name="currentPassword"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        helperText="Digite sua senha atual"
                        sx={textFieldStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Nova Senha"
                        name="newPassword"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        helperText="Mínimo 6 caracteres"
                        sx={textFieldStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Confirmar Nova Senha"
                        name="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        helperText={
                          passwordData.newPassword && passwordData.confirmPassword 
                            ? (passwordData.newPassword === passwordData.confirmPassword 
                                ? "✅ Senhas coincidem" 
                                : "❌ Senhas não coincidem")
                            : "Digite a nova senha novamente"
                        }
                        error={passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                        sx={textFieldStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="button"
                        variant="contained"
                        color="error"
                        onClick={handleChangePassword}
                        sx={{
                          '&:hover': { 
                            bgcolor: 'error.dark',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                        }}
                      >
                        Alterar Senha
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={updateProfileMutation.isPending ? <CircularProgress size={20} /> : <Save />}
            disabled={updateProfileMutation.isPending}
            sx={{ bgcolor: '#9D00FF', '&:hover': { bgcolor: '#C733FF' } }}
          >
            {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Upload de Foto de Perfil */}
      <Dialog open={photoDialog} onClose={handleClosePhotoDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Alterar Foto de Perfil
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Formatos aceitos:</strong> JPEG, JPG, PNG<br/>
                <strong>Tamanho máximo:</strong> 5MB<br/>
                <strong>Dimensões recomendadas:</strong> 400x400px
              </Typography>
            </Alert>

            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              {previewUrl ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                    Preview da Foto:
                  </Typography>
                  <Avatar
                    src={previewUrl}
                    sx={{ width: 150, height: 150, bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                  />
                </Box>
              ) : (
                <Box textAlign="center">
                  <PhotoCamera sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Nenhuma foto selecionada
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{ borderColor: '#9D00FF', color: '#9D00FF' }}
              >
                Selecionar Foto
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFile && (
                <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center' }}>
                  Arquivo selecionado: {selectedFile.name}<br/>
                  Tamanho: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosePhotoDialog}
            startIcon={<Cancel />}
            disabled={uploadPhotoMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUploadPhoto}
            variant="contained"
            disabled={!selectedFile || uploadPhotoMutation.isPending}
            sx={{ bgcolor: '#9D00FF', '&:hover': { bgcolor: '#C733FF' } }}
          >
            {uploadPhotoMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              'Alterar Foto'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DJProfileManagement;
